// Client-side domain rules for the store form.
//
// These mirror what store-manager enforces on save, so the user is told what is wrong
// while typing instead of after a failed save:
//   - Domain.isInvalid()                      -> HOSTNAME
//   - CERTIFICATE_RESOURCE_ID_REGEX           -> CERT_RESOURCE_ID (cert-managed frontends only)
//   - DOMAIN_IN_MULTIPLE_GROUPINGS            -> duplicate within the same store
// The server stays the authority; this only stops the obvious cases earlier.

// com.mysagestore...model.Domain#FULL_DOMAIN_NAME_REGEX
export const HOSTNAME = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$/;

// GCP Certificate Manager resource ids, after the hostname's dots become dashes
export const CERT_RESOURCE_ID = /^[a-z][-a-z0-9]{0,61}[a-z0-9]$/;

// a single hostname label — what the staging field takes before the environment suffix
export const HOSTNAME_LABEL = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/;

const certifiable = (hostname) => CERT_RESOURCE_ID.test(hostname.toLowerCase().replace(/\./g, '-'));

/**
 * @returns an error message, or undefined when the domain is acceptable
 */
export function domainError(value, { certManaged = false, otherNames = [], requireFqdn = true } = {}) {
  const name = (value || '').trim();

  if (!name) return 'Enter a domain';
  // protocol and path first: for a pasted URL those name the actual mistake
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(name)) return 'Enter the domain only, without https://';
  if (name.includes('/')) return 'Enter the domain only, without a path';
  if (/\s/.test(name)) return 'A domain cannot contain spaces';
  if (name.includes('..')) return 'A domain cannot contain two dots in a row';
  if (name.startsWith('.') || name.endsWith('.')) return 'A domain cannot start or end with a dot';
  if (name.startsWith('-') || name.endsWith('-')) return 'A domain cannot start or end with a hyphen';
  if (!HOSTNAME.test(name)) return 'Not a valid domain — use letters, numbers and hyphens, like shop.example.com';
  if (requireFqdn && !name.includes('.')) return 'Enter the full domain, like shop.example.com';
  if (certManaged && !certifiable(name)) {
    return name.length > 63
      ? 'Too long for a managed TLS certificate — use at most 63 characters'
      : 'Cannot hold a managed TLS certificate — the domain must start with a letter';
  }
  if (otherNames.some((other) => (other || '').trim().toLowerCase() === name.toLowerCase())) {
    return 'Already used by this store — a domain can belong to only one grouping';
  }

  return undefined;
}

/**
 * The staging field takes one label (the environment suffix is appended on save), so the
 * dotted-domain rules do not apply — but a cert-managed grouping still needs a leading letter.
 */
export function stagingHostError(value, { certManaged = false, suffix = '' } = {}) {
  const host = (value || '').trim();

  if (!host) return 'Enter a hostname';
  if (/\s/.test(host)) return 'A hostname cannot contain spaces';
  if (host.includes('.')) return 'Enter the first part only — the rest is added for you';
  if (!HOSTNAME_LABEL.test(host)) return 'Use letters, numbers and hyphens only';
  if (certManaged && !certifiable(suffix ? `${host}.${suffix}` : host)) {
    return 'Cannot hold a managed TLS certificate — the hostname must start with a letter and the full domain be at most 63 characters';
  }

  return undefined;
}

// saveErrorMessage moved to src/utils/apiErrors.js — it is response-shape mapping, and
// `actions/` needs it without importing a view module.
